import { Field, Form, useFormikContext } from 'formik';
import isEqual from 'lodash-es/isEqual';
import React from 'react';
import { useLocation } from 'react-router-dom';
import { Dashboard, PageLayout } from '@components/common';
import { DashboardBackButton, FeatureFlagDecorator, useDashboardFeatureFlags, useDashboardPermissions, } from '@components/utils';
import { LeavingGuard } from '@components/utils/LeavingGuard';
import { Fieldset, HeadContent, Loading, SaveShortcutListener, Select, Subsection, } from '@fourthwall/components';
import { Offer } from '@fourthwall/services/dist/models/offer';
import { useFocusFirstErrorOnSubmit } from '@fourthwall/utils/dist/formik';
import { routing } from '@utils/routing';
import { addProtocol } from '@utils/url';
import { ConfirmBar } from '../../modules/ConfirmBar';
import { LABEL_BY_FULFILLMENT_SERVICE, LABEL_BY_MANUFACTURING_SERVICE } from '../Product.consts';
import { getFulfillmentType } from '../Product.utils';
import { ProductFiles } from '../ProductFiles/ProductFiles';
import { ProductBanners } from './ProductBanners';
import { ProductBespokeProductId } from './ProductBespokeProductId/ProductBespokeProductId';
import { ProductOrdersContainer } from './ProductOrders';
import { ProductPricing } from './ProductPricing';
import { ProductDetails, ProductToolbar } from './components';
import { DesignEditButton } from './modules/designEditButton/DesignEditButton';
import { DownloadArtFilesButton } from './modules/downloadArtFilesButton/DownloadArtFilesButton';
import { Shipping } from './modules/shipping/Shipping';
import { Variants } from './modules/variants';
var FULFILLING_SERVICES = Offer.FULFILLING_SERVICES;
var MANUFACTURING_SERVICES = Offer.MANUFACTURING_SERVICES;
export const SingleProductView = ({ productId, productCustomizationId, productIsDeleting, productUpdateIsSubmitting, offerImages, images, offerType, productBanners, rightContent, name, status, slug, productIsFetching, shippingFlatRatesAreFetching, productStatusIsUpdating, productStockIsSubmitting, isUploadingImages, shopBaseUri, maxDigitalFilesSize, isTotalDigitalFilesSizeExceeded, isFreeProduct, pricingType, showPricingPerVariant, togglePricingType, applyToAllVariants, onChangeUrl, onOrderSamples, onArchive, onUnarchive, }) => {
    const location = useLocation();
    const { dashboardFeatureFlags } = useDashboardFeatureFlags();
    const { hasPermission } = useDashboardPermissions();
    const { values, initialValues, isSubmitting, isValid, submitForm, resetForm } = useFormikContext();
    useFocusFirstErrorOnSubmit();
    const isUploadingFiles = values.digitalItems.some((item) => item.isUploading);
    const fulfillmentType = getFulfillmentType(values.fulfillmentService);
    const isFormEditable = hasPermission('products.edit.details');
    const productUrl = `${shopBaseUri}/products/${slug}`;
    const isFormDirty = !isEqual(initialValues, values);
    const renderOfferImages = () => {
        if (!offerImages)
            return null;
        return (React.createElement(Subsection, { title: fulfillmentType === 'DIGITAL' ? 'Photography' : 'Photography and design' },
            offerImages,
            productCustomizationId && isFormEditable && (React.createElement("div", { style: { marginTop: 16 } },
                React.createElement(DesignEditButton, { offerId: productId, customizationId: productCustomizationId }),
                React.createElement("span", { style: { marginLeft: 16 } },
                    React.createElement(DownloadArtFilesButton, { customizationId: productCustomizationId }))))));
    };
    const renderBottomContent = () => {
        if (!images)
            return null;
        return (React.createElement(React.Fragment, null,
            React.createElement(Variants, { applyToAllVariants: applyToAllVariants }),
            React.createElement(ProductOrdersContainer, { offerId: productId })));
    };
    const renderLeftContent = () => {
        const shouldRenderFiles = () => {
            switch (fulfillmentType) {
                case 'ON_DEMAND':
                case 'SCREEN_PRINTED':
                case 'FULFILLED_BY_CREATOR':
                    return false;
                case 'DIGITAL':
                    return true;
            }
        };
        const renderPricing = () => {
            if (showPricingPerVariant) {
                return null;
            }
            return (React.createElement(ProductPricing, { pricingType: pricingType, togglePricingType: togglePricingType, applyToAllVariants: applyToAllVariants }));
        };
        const renderManufacturer = () => {
            const options = MANUFACTURING_SERVICES.filter((value) => value !== 'DIGITAL_ITEM').map((value) => ({ label: LABEL_BY_MANUFACTURING_SERVICE[value], value }));
            return (React.createElement(Fieldset.Row, null,
                React.createElement(Fieldset.Item, null,
                    React.createElement(Field, { component: Select, name: "manufacturingService", label: "Manufacturer", options: options, disabled: !isFormEditable }),
                    values.manufacturingService === 'FOURTHWALL_BESPOKE' && (React.createElement(ProductBespokeProductId, { bespokeProductId: values.bespokeProductId })))));
        };
        const renderFulfiller = () => {
            const options = FULFILLING_SERVICES.filter((value) => value !== 'DIGITAL_ITEM')
                .map((value) => ({ label: LABEL_BY_FULFILLMENT_SERVICE[value], value }))
                .sort((a, b) => a.label.localeCompare(b.label));
            return (React.createElement(Fieldset.Row, null,
                React.createElement(Fieldset.Item, null,
                    React.createElement(Field, { component: Select, name: "fulfillmentService", label: "Fulfiller", options: options, disabled: !isFormEditable }))));
        };
        return (React.createElement(React.Fragment, null,
            React.createElement(ProductBanners, { ...productBanners }),
            React.createElement(ProductDetails, { productId: productId, isFormEditable: isFormEditable, showCategories: ['DIGITAL', 'MANUAL'].includes(offerType) }),
            React.createElement(Subsection, null, renderPricing()),
            shouldRenderFiles() && (React.createElement(Subsection, { title: "Files delivered on purchase" },
                React.createElement(ProductFiles, { maxSize: maxDigitalFilesSize, isTotalDigitalFilesSizeExceeded: isTotalDigitalFilesSizeExceeded, isFreeProduct: isFreeProduct, withMembersOnly: values.withMembersOnly, offerId: productId, files: values.digitalItems, isFormEditable: isFormEditable }))),
            React.createElement(FeatureFlagDecorator, { ...dashboardFeatureFlags.productAdvancedOptions, wrapper: Subsection },
                React.createElement(Subsection, { title: "Manufacturer and Fulfiller" },
                    React.createElement(Fieldset, null,
                        renderManufacturer(),
                        renderFulfiller()))),
            React.createElement(Shipping, null),
            renderOfferImages()));
    };
    return (React.createElement(React.Fragment, null,
        React.createElement(Loading, { isLoading: isSubmitting ||
                productIsFetching ||
                shippingFlatRatesAreFetching ||
                productUpdateIsSubmitting ||
                productIsDeleting ||
                productStatusIsUpdating ||
                productStockIsSubmitting },
            React.createElement(HeadContent, { title: name }),
            React.createElement(Form, null,
                React.createElement(PageLayout, { leftContent: renderLeftContent(), rightContent: rightContent, bottomContent: renderBottomContent() },
                    React.createElement(Dashboard.ItemViewHeader, { name: name, link: {
                            url: addProtocol(productUrl),
                            label: addProtocol(productUrl),
                            copyUrl: addProtocol(productUrl),
                        }, status: status, toolbar: React.createElement(ProductToolbar, { onChangeUrl: onChangeUrl, onOrderSamples: onOrderSamples, onArchive: onArchive, onUnarchive: onUnarchive }), backButton: React.createElement(DashboardBackButton, { label: "Products", to: {
                                pathname: routing.products.all.self,
                                search: location.search,
                            } }) })))),
        React.createElement(SaveShortcutListener, { disabled: !isFormDirty, onSave: submitForm }),
        React.createElement(ConfirmBar, { isOpen: isFormDirty || isUploadingImages || productUpdateIsSubmitting, isLoading: isUploadingFiles || isUploadingImages || productUpdateIsSubmitting || isSubmitting, onConfirm: submitForm, onCancel: () => resetForm({ values: initialValues }) }),
        React.createElement(LeavingGuard, { when: (isFormDirty && !!productId) || isUploadingImages, onSave: submitForm, isValid: isValid })));
};
